import {
  removeCleanOrNotDefined,
  showCmsFormValidationErrorToast,
  useCreateLoginPage,
  useCreateSignUpPage,
  useLoginFormData,
  useSignUpFormData,
  useUpdateLoginPage,
  useUpdateSignUpPage,
  wrapHandleSubmitWithAction,
} from "@/api/cms";
import { CmsPageAppearanceConfig } from "@/components/cms/theme/CmsPageAppearanceConfig";
import { LoginSignupForm } from "@/components/cms/LoginSignupForm";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import { useToast } from "@/hooks/use-toast";
import {
  constructCmsLocaleDict,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import {
  cmsPageLocaleBaseSchemaShape,
  loginContentSchema,
  signUpContentSchema,
  withPageNameRequiredWhenPublished,
} from "@/lib/cms/cms-types";
import { consumeDrawerAppearanceSaveErrorToastShown } from "@/lib/cms/cms-page-appearance-drawer-save";
import {
  buildCmsPagePatchPayload,
  isAnyCmsLocaleThemeDirty,
  isCmsLocaleThemeDirty,
  pageThemeFromDto,
} from "@/lib/cms/page-theme";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef } from "react";
import { FormProvider, Path, useForm } from "react-hook-form";
import { FormFieldVisibilityProvider } from "@/context/FormFieldVisibilityContext";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = z.object({
  login: multilingualFieldSchema(
    withPageNameRequiredWhenPublished(
      cmsPageLocaleBaseSchemaShape.merge(loginContentSchema.omit({ id: true })),
    ),
  ),
  signup: multilingualFieldSchema(
    cmsPageLocaleBaseSchemaShape
      .omit({ published: true })
      .merge(signUpContentSchema.omit({ id: true })),
  ),
  //   published: multilingualFieldSchema(baseCmsFormSchema.shape.published),
});

type FormData = z.infer<typeof formSchema>;

const AuthCMS = () => {
  const { t } = useTranslation();
  const { websiteLanguages, defaultWebsiteLanguage } = useCmsWebsiteLanguages();

  const { toast } = useToast();

  const { data: loginFormData } = useLoginFormData();
  const createLogin = useCreateLoginPage();
  const updateLogin = useUpdateLoginPage();

  const { data: signupFormData } = useSignUpFormData();
  const createSignup = useCreateSignUpPage();
  const updateSignup = useUpdateSignUpPage();

  const defaultLoginValues: FormData["login"] = useMemo(() => {
    return constructCmsLocaleDict((lang) => {
      const login = loginFormData?.[lang];
      return {
        published: login?.published ?? false,
        pageName: login?.pageName ?? "",
        ...pageThemeFromDto(login),
        subtitle: login?.content?.subtitle,
      };
    }, websiteLanguages);
  }, [loginFormData, websiteLanguages]);

  const defaultSignupValues: FormData["signup"] = useMemo(() => {
    return constructCmsLocaleDict((lang) => {
      const signup = signupFormData?.[lang];
      return {
        pageName: signup?.pageName ?? "",
        ...pageThemeFromDto(signup),
        subtitle: signup?.content?.subtitle,
      };
    }, websiteLanguages);
  }, [signupFormData, websiteLanguages]);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      login: defaultLoginValues,
      signup: defaultSignupValues,
    },
  });

  const hasInitLogin = useRef(false);
  const hasInitSignup = useRef(false);

  useEffect(() => {
    if (loginFormData != null && !hasInitLogin.current && Object.keys(defaultLoginValues).length > 0) {
      form.reset({
        ...form.getValues(),
        login: defaultLoginValues,
      });
      hasInitLogin.current = true;
    }
  }, [defaultLoginValues, form, loginFormData]);

  useEffect(() => {
    if (signupFormData != null && !hasInitSignup.current && Object.keys(defaultSignupValues).length > 0) {
      form.reset({
        ...form.getValues(),
        signup: defaultSignupValues,
      });
      hasInitSignup.current = true;
    }
  }, [defaultSignupValues, form, signupFormData]);

  const onSubmit = async (submittedData: FormData) => {
    try {
    const refLoginTheme = submittedData.login[defaultWebsiteLanguage];
    const anyLoginThemeDirty = isAnyCmsLocaleThemeDirty(
      form,
      "login",
      websiteLanguages,
    );
    const anySignupThemeDirty = isAnyCmsLocaleThemeDirty(
      form,
      "signup",
      websiteLanguages,
    );
    const refSignupTheme = submittedData.signup[defaultWebsiteLanguage];

    await Promise.all(
      Object.keys(submittedData.login).map(async (key) => {
        const localeData = submittedData.login[key as SupportedLocale];
        const baseData = loginFormData?.[key as SupportedLocale];
        const loginPath = `login.${key}`;
        const loginDirty = isCmsLocaleThemeDirty(form, loginPath);
        const shouldSaveLogin = localeData && (loginDirty || anyLoginThemeDirty);
        if (!shouldSaveLogin) return;

        const mutation = baseData?.content?.id ? updateLogin : createLogin;

        const {
          pageName,
          published,
          pageBackgroundColorId: localeBg,
          pageTextColorMode: localeMode,
          pageTextColorId: localeTextId,
          ...content
        } = localeData;
        const pageNameStr = pageName ?? "";

        await mutation.mutateAsync({
          id: baseData?.content?.id ?? "",
          lang: key as SupportedLocale,
          data: buildCmsPagePatchPayload({
            pageName: pageNameStr,
            published,
            pageBackgroundColorId:
              refLoginTheme?.pageBackgroundColorId ?? localeBg,
            pageTextColorMode: refLoginTheme?.pageTextColorMode ?? localeMode,
            pageTextColorId: refLoginTheme?.pageTextColorId ?? localeTextId,
            content,
          }),
        });
      }),
    );

    await Promise.all(
      Object.keys(submittedData.signup).map(async (key) => {
        const localeData = submittedData.signup[key as SupportedLocale];
        const baseData = signupFormData?.[key as SupportedLocale];
        const signupPath = `signup.${key}`;
        const signupDirty = isCmsLocaleThemeDirty(form, signupPath);
        const publishedDirty = form.getFieldState(
          `login.${key}.published` as Path<FormData>,
        ).isDirty;
        const shouldSaveSignup =
          localeData &&
          (signupDirty || anySignupThemeDirty || publishedDirty);
        if (!shouldSaveSignup) return;

        const mutation = baseData?.content?.id ? updateSignup : createSignup;

        const {
          pageName,
          pageBackgroundColorId: localeBg,
          pageTextColorMode: localeMode,
          pageTextColorId: localeTextId,
          ...content
        } = localeData;

        await mutation.mutateAsync(
        {
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: buildCmsPagePatchPayload({
            pageName: pageName ?? "",
            published:
              submittedData.login?.[key as SupportedLocale]?.published ?? false,
            pageBackgroundColorId:
              refSignupTheme?.pageBackgroundColorId ?? localeBg,
            pageTextColorMode: refSignupTheme?.pageTextColorMode ?? localeMode,
            pageTextColorId: refSignupTheme?.pageTextColorId ?? localeTextId,
            content,
          }),
        });
      }),
    );

    toast({
      title: t("success"),
      description: t("AuthentificationUpdatedSucess"),
      duration: 4000,
      variant: "success",
    });
    } catch (error) {
      console.error("Error saving login/signup CMS page:", error);
      if (!consumeDrawerAppearanceSaveErrorToastShown()) {
        toast({
          title: t("error"),
          description: t("cms.appearance.error.save"),
          variant: "error",
          duration: 4000,
        });
      }
    }
  };
  const cleanBeforeSubmit = () => {
    removeCleanOrNotDefined(form, loginFormData, "login");
    removeCleanOrNotDefined(form, signupFormData, "signup");
  };

  return (
    <CMSLayout
      pageName={t("cmsTabAuthentification")}
      havePublishedSwitch={false}
      formIds={["login-signup"]}
    >
      <FormProvider {...form}>
        <FormFieldVisibilityProvider formKey="cms-login-signup">
        <CmsPageAppearanceConfig formId="login-signup" />
        <form
          onSubmit={wrapHandleSubmitWithAction(
            form,
            cleanBeforeSubmit,
            onSubmit,
            showCmsFormValidationErrorToast,
          )}
          id="login-signup"
          className="flex w-full flex-col gap-6"
        >
          <DoubleMultilingualField
            className="w-full"
            corner={(lang) => (
              <Switch dispLabel={false} name={`login.${lang}.published`} />
            )}
            render={(lang) => <LoginSignupForm language={lang} />}
          />
        </form>
              </FormFieldVisibilityProvider>
      </FormProvider>
    </CMSLayout>
  );
};

export default AuthCMS;
